/**
 * What a turn's error card shows when OpenAI failed it on a ChatGPT plan: the plan route's own
 * codes, which the neutral `errors.ts` finds through the descriptor's `errors` and asks about
 * here. Client-safe: no server imports.
 */
import type { AiSubscriptionFirewallRefusals } from '../../policy';
import type { AiSubscriptionErrorCard, AiSubscriptionErrorMatcher } from '../../providers';

import { getRecord } from '@/lib/utils/unknown';

const CHATGPT_UNAVAILABLE_ERROR: AiSubscriptionErrorCard = {
  message: 'ChatGPT isn’t available right now. Try again in a few minutes.',
  action: null,
  retry: true,
};

/** OpenAI couldn't read the person's plan or workspace for a moment. */
const PLAN_UNAVAILABLE_ERROR: AiSubscriptionErrorCard = {
  message: 'Your ChatGPT plan isn’t available right now. Try again in a few minutes.',
  action: null,
  retry: true,
};

/** The plan route doesn't take the request's method or endpoint; the same request fails again. */
const ROUTE_NOT_SUPPORTED_ERROR: AiSubscriptionErrorCard = {
  message:
    'OpenAI doesn’t take this kind of request from cubic on a ChatGPT plan. Sending it again won’t work.',
  action: null,
  retry: false,
};

const UNSUPPORTED_CAPABILITY = 'subscription_sharing_unsupported_capability';

/**
 * OpenAI's observed answer for an access token revoked after disconnecting ChatGPT in cubic:
 * a code other OAuth APIs use too.
 */
const TOKEN_REVOKED = 'token_revoked';

/** The plan route refused Fast mode's `service_tier`; with Fast mode off, the message goes through. */
const FAST_MODE_REFUSED_ERROR: AiSubscriptionErrorCard = {
  message:
    'Your ChatGPT plan doesn’t allow Fast mode for this request. Turn off Fast mode, then send your message again.',
  action: null,
  retry: true,
};

/**
 * The request used something the plan route doesn't support, which OpenAI names in `error.param`
 * when it can; the same request fails again, so Retry is off.
 */
function unsupportedCapabilityError(param: string | null): AiSubscriptionErrorCard {
  return {
    message:
      param === null
        ? 'Your ChatGPT plan doesn’t support part of this request. Sending it again won’t work.'
        : `Your ChatGPT plan doesn’t support part of this request (${param}). Sending it again won’t work.`,
    action: null,
    retry: false,
  };
}

/** OpenAI's plan route codes whose card never varies, each as its card shows it. */
const PLAN_ROUTE_ERRORS: ReadonlyMap<string, AiSubscriptionErrorCard> = new Map([
  [
    'subscription_sharing_usage_limit_exceeded',
    {
      message:
        'You’ve reached your ChatGPT plan’s usage limit. ChatGPT Settings → Usage shows when it resets.',
      action: 'usage',
      retry: false,
    },
  ],
  ['subscription_sharing_usage_unavailable', CHATGPT_UNAVAILABLE_ERROR],
  ['subscription_sharing_user_unavailable', PLAN_UNAVAILABLE_ERROR],
  [
    'subscription_sharing_user_not_eligible',
    {
      message:
        'This ChatGPT account’s plan can’t run cubic sessions. Reconnect ChatGPT with a Plus or Pro plan.',
      action: 'reconnect',
      retry: true,
    },
  ],
  ['subscription_sharing_route_not_supported', ROUTE_NOT_SUPPORTED_ERROR],
]);

/**
 * A request field path as OpenAI names one, such as `tools[0].type`; anything else is not shown.
 */
const ERROR_PARAM = /^[\w.[\]-]{1,80}$/;

/**
 * The `param` OpenAI gave beside `code` in a raw error body: an HTTP refusal's
 * `{ error: { code, param } }`, a stream's `error` event or a `response.failed` event's
 * `response.error`. Null when the body is not JSON or names none.
 */
function errorParamOf(body: string, code: string): string | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(body);
  } catch {
    return null;
  }
  const errors = [parsed, getRecord(parsed)?.error, getRecord(getRecord(parsed)?.response)?.error];
  for (const candidate of errors) {
    const error = getRecord(candidate);
    if (error?.code === code && typeof error.param === 'string' && ERROR_PARAM.test(error.param)) {
      return error.param;
    }
  }
  return null;
}

/**
 * A plan route code's card. `subscription_sharing_invalid_user`, the `chatpass_v2_*` codes and
 * `token_revoked`, which name a sign-in OpenAI no longer accepts for the request, read as the
 * firewall's `needs_reconnect` refusal, and `subscription_sharing_unsupported_capability` as
 * `unsupportedCapabilityError`, which names the field the error names, or as Fast mode's card when
 * that field is `service_tier`.
 */
function chatGptErrorCard(
  code: string,
  body: string,
  refusals: AiSubscriptionFirewallRefusals,
): AiSubscriptionErrorCard | null {
  if (code === UNSUPPORTED_CAPABILITY) {
    const param = errorParamOf(body, code);
    return param === 'service_tier' ? FAST_MODE_REFUSED_ERROR : unsupportedCapabilityError(param);
  }
  if (
    code === 'subscription_sharing_invalid_user' ||
    code === TOKEN_REVOKED ||
    code.startsWith('chatpass_v2_')
  ) {
    const { message, action, retry } = refusals.needs_reconnect;
    return { message, action, retry };
  }
  return PLAN_ROUTE_ERRORS.get(code) ?? null;
}

/**
 * How OpenAI names a plan's failures: `subscription_sharing_*` and `chatpass_v2_*` codes, and in
 * a plan session `token_revoked`.
 */
export const CHATGPT_ERRORS: AiSubscriptionErrorMatcher = {
  codePrefixes: ['subscription_sharing_', 'chatpass_v2_'],
  sessionCodes: [TOKEN_REVOKED],
  card: chatGptErrorCard,
};
